/** "Where is this used" panel — the cross-reference viewer shared by every
 *  satellite catalog editor (regions, trip types, …).
 *
 * Fetches `GET /{resource}/{id}/used_by/` (paged, filterable by object type and
 * a debounced free-text search) and renders a compact DataGrid: Type / ID /
 * Name. Name links inline to the object's dashboard page (new tab) when the
 * backend supplies a `dashboard_link`, else to its console page when
 * `consoleRouteFor` knows the type — except the `CONSOLE_FIRST_TYPES` (tours,
 * catalog itineraries), whose name opens their console page; its hover popover
 * offers every target the row has ("Open in the console" / "Open in the
 * dashboard" / "Open in Django admin") plus copy. A row whose type has
 * neither (an activity's orphan day plan) is plain text. A type-filter Select
 * is fed by the response's `available_types`; a count-only type (an activity's
 * unlisted trips) is never a filter value — it is a note above the grid. A
 * refresh button re-runs the query.
 */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  fetchUsedBy,
  type UsedByRef,
  type UsedByResource,
  type UsedByTypeOption,
} from '@/api/usedBy';
import {
  act,
  CopyableId,
  ValueActions,
  type ValueAction,
} from '@/components/ValueActions/ValueActions';
import { consoleRouteFor } from '@/utils/gridLinks';

const PAGE_SIZE = 25;

/** Types whose name opens their console page even when the row has a `dashboard_link`: the
 *  console has the record's own page, where the operator works on it (a tour, a catalog
 *  itinerary). Their dashboard page stays one step away, in the popover. Every other type
 *  keeps the dashboard-first link. */
const CONSOLE_FIRST_TYPES: ReadonlySet<string> = new Set(['demo_group_tour', 'demo_itinerary_group']);

/** Inline link + popover for one referencing record. */
function NameCell({ row }: { row: UsedByRef }) {
  const consoleTo = consoleRouteFor(row.type_key, row.id);
  const consoleFirst = consoleTo !== null && CONSOLE_FIRST_TYPES.has(row.type_key);
  const actions: ValueAction[] = [];
  if (consoleTo) actions.push(act.open(consoleTo, 'Open in the console'));
  if (row.dashboard_link) actions.push(act.external(row.dashboard_link, 'Open in the dashboard'));
  if (row.admin_link) actions.push(act.external(row.admin_link, 'Open in Django admin'));
  actions.push(act.copy(String(row.id), 'Copy id'));

  // The name's own link: the console page first for CONSOLE_FIRST_TYPES, else the dashboard.
  const dashboardLink = consoleFirst ? null : row.dashboard_link;
  const text = dashboardLink ? (
    <MuiLink
      href={dashboardLink}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {row.description}
      <OpenInNewIcon sx={{ fontSize: 14 }} />
    </MuiLink>
  ) : consoleTo ? (
    <MuiLink component={RouterLink} to={consoleTo} underline="hover">
      {row.description}
    </MuiLink>
  ) : (
    row.description
  );
  return <ValueActions actions={actions}>{text}</ValueActions>;
}

/** "1,000+ trips not listed — …": the words for a count-only type. */
function countOnlyNote(t: UsedByTypeOption): string {
  const n = t.count ?? 0;
  const count = `${n.toLocaleString('en-US')}${t.capped ? '+' : ''}`;
  if (t.key === 'demo_user_itinerary') {
    const trips = n === 1 && !t.capped ? 'trip' : 'trips';
    return `${count} ${trips} not listed — travellers' own trips and trips of deleted tours`;
  }
  return `${count} not listed · ${t.name}`;
}

export function UsedByPanel({ resource, id }: { resource: UsedByResource; id: number }) {
  const [type, setType] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  // Debounce the free-text search; reset to the first page on a new query.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const query = useQuery({
    queryKey: ['usedBy', resource, id, { type, search, page }],
    queryFn: () =>
      fetchUsedBy(resource, id, {
        type: type || undefined,
        search: search || undefined,
        page: page + 1, // DRF is 1-based
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.results ?? [];
  const allTypes = query.data?.available_types ?? [];
  const availableTypes = allTypes.filter((t) => !t.count_only);
  const countOnly = allTypes.filter((t) => t.count_only && (t.count ?? 0) > 0);

  const columns = useMemo<GridColDef<UsedByRef>[]>(
    () => [
      { field: 'type_name', headerName: 'Type', width: 200, sortable: false },
      {
        field: 'id',
        headerName: 'ID',
        width: 90,
        sortable: false,
        renderCell: (p) => <CopyableId value={p.row.id} />,
      },
      {
        field: 'description',
        headerName: 'Name',
        flex: 1,
        minWidth: 220,
        sortable: false,
        renderCell: (p) => <NameCell row={p.row} />,
      },
    ],
    [],
  );

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Type"
          value={type}
          sx={{ width: 220 }}
          onChange={(e) => {
            setType(e.target.value);
            setPage(0);
          }}
        >
          <MenuItem value="">All types</MenuItem>
          {availableTypes.map((t) => (
            <MenuItem key={t.key} value={t.key}>
              {t.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Search"
          value={searchInput}
          placeholder="Name or #id"
          sx={{ width: 240 }}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => void query.refetch()} aria-label="Refresh">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {countOnly.map((t) => (
        <Stack
          key={t.key}
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mb: 1.5, color: 'text.secondary' }}
        >
          <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16 }} />
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {countOnlyNote(t)}
          </Typography>
        </Stack>
      ))}

      <DataGridPro<UsedByRef>
        rows={rows}
        columns={columns}
        // `id` (the referenced object's pk) is not unique across types — an
        // itinerary_group #5 and a vehicle_route_set #5 would collide. Key rows
        // by type_key:id so the grid never merges/drops distinct references.
        getRowId={(row) => `${row.type_key}:${row.id}`}
        rowCount={query.data?.count ?? 0}
        loading={query.isLoading || query.isFetching}
        autoHeight
        disableColumnMenu
        pagination
        paginationMode="server"
        pageSizeOptions={[PAGE_SIZE]}
        paginationModel={{ page, pageSize: PAGE_SIZE }}
        onPaginationModelChange={(m) => setPage(m.page)}
        slots={{
          noRowsOverlay: () => (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              {countOnly.length
                ? 'No listed records reference this.'
                : 'No records reference this.'}
            </Box>
          ),
        }}
      />
    </Box>
  );
}
